/**
 * Synthia 插件管理器实现
 * 负责插件的加载、安装、卸载和管理
 */

import {
  SynthiaPlugin,
  PluginManager,
  PluginRegistry,
  PluginMetadata,
  PluginContext,
  PluginError,
  PluginSystemConfig,
} from './plugin.js';
import { DefaultPluginRegistry } from './plugin-registry.js';
import fs from 'fs-extra';
import * as path from 'path';
import * as os from 'os';

/**
 * 插件管理器实现
 */
export class DefaultPluginManager implements PluginManager {
  public registry: PluginRegistry;
  private config: PluginSystemConfig;
  private context: PluginContext;

  constructor(config: Partial<PluginSystemConfig> = {}) {
    this.config = this.mergeConfig(config);
    this.registry = new DefaultPluginRegistry();
    this.context = this.createContext();
  }

  /**
   * 加载插件
   */
  async loadPlugin(pluginPath: string): Promise<SynthiaPlugin> {
    try {
      // 检查插件文件是否存在
      if (!(await fs.pathExists(pluginPath))) {
        throw new PluginError(`插件文件不存在: ${pluginPath}`);
      }

      // 动态导入插件
      const pluginModule = await import(pluginPath);

      // 获取插件实例
      let plugin: SynthiaPlugin;
      if (pluginModule.default) {
        // 检查是否是嵌套的默认导出
        if (pluginModule.default.default) {
          plugin = pluginModule.default.default;
        } else {
          plugin = pluginModule.default;
        }
      } else if (pluginModule.plugin) {
        plugin = pluginModule.plugin;
      } else {
        throw new PluginError(`插件文件 ${pluginPath} 没有导出有效的插件实例`);
      }

      // 验证插件结构
      if (!plugin.metadata || !plugin.commands) {
        throw new PluginError(`插件文件 ${pluginPath} 结构不正确`);
      }

      // 初始化插件
      if (plugin.initialize) {
        await plugin.initialize(this.context);
      }

      // 注册插件
      await this.registry.register(plugin);

      return plugin;
    } catch (error) {
      throw new PluginError(
        `加载插件失败: ${error instanceof Error ? error.message : String(error)}`,
        undefined,
        'LOAD_FAILED'
      );
    }
  }

  /**
   * 卸载插件
   */
  async unloadPlugin(pluginName: string): Promise<void> {
    try {
      const plugin = this.registry.get(pluginName);
      if (!plugin) {
        throw new PluginError(`插件 ${pluginName} 不存在`);
      }

      // 执行清理钩子
      if (plugin.cleanup) {
        await plugin.cleanup();
      }

      // 注销插件
      await this.registry.unregister(pluginName);

      console.log(`✅ 插件 ${pluginName} 卸载成功`);
    } catch (error) {
      throw new PluginError(
        `卸载插件失败: ${error instanceof Error ? error.message : String(error)}`,
        pluginName,
        'UNLOAD_FAILED'
      );
    }
  }

  /**
   * 发现插件
   */
  async discoverPlugins(searchPaths: string[]): Promise<string[]> {
    const pluginPaths: string[] = [];

    for (const searchPath of searchPaths) {
      try {
        if (!(await fs.pathExists(searchPath))) {
          continue;
        }

        const items = await fs.readdir(searchPath);

        for (const item of items) {
          const itemPath = path.join(searchPath, item);
          const stat = await fs.stat(itemPath);

          if (stat.isDirectory()) {
            // 检查是否是插件目录
            const packageJsonPath = path.join(itemPath, 'package.json');
            if (await fs.pathExists(packageJsonPath)) {
              const packageJson = await fs.readJson(packageJsonPath);
              if (packageJson.synthiaPlugin) {
                // 查找插件的 plugin.js 文件
                const pluginFile = 'plugin.js';
                const pluginPath = path.join(itemPath, pluginFile);
                if (await fs.pathExists(pluginPath)) {
                  pluginPaths.push(pluginPath);
                } else {
                  // 尝试其他可能的插件文件位置
                  const possibleEntries = [
                    'dist/plugin.js',
                    'lib/plugin.js',
                    'src/plugin.js',
                  ];
                  for (const entry of possibleEntries) {
                    const testPath = path.join(itemPath, entry);
                    if (await fs.pathExists(testPath)) {
                      pluginPaths.push(testPath);
                      break;
                    }
                  }
                }
              }
            }
          } else if (stat.isFile() && item.endsWith('.js')) {
            // 检查是否是插件文件
            pluginPaths.push(itemPath);
          }
        }
      } catch (error) {
        console.warn(`扫描插件路径失败: ${searchPath}`, error);
      }
    }

    return pluginPaths;
  }

  /**
   * 安装插件
   */
  async installPlugin(pluginName: string, version?: string): Promise<void> {
    try {
      // 检查插件是否已安装
      if (this.registry.has(pluginName)) {
        throw new PluginError(`插件 ${pluginName} 已安装`);
      }

      // 从 npm 安装插件
      const installPath = path.join(this.config.installDir, pluginName);
      await this.installFromNpm(pluginName, installPath, version);

      // 加载插件
      const pluginPath = path.join(installPath, 'index.js');
      await this.loadPlugin(pluginPath);

      console.log(`✅ 插件 ${pluginName} 安装成功`);
    } catch (error) {
      throw new PluginError(
        `安装插件失败: ${error instanceof Error ? error.message : String(error)}`,
        pluginName,
        'INSTALL_FAILED'
      );
    }
  }

  /**
   * 卸载插件
   */
  async uninstallPlugin(pluginName: string): Promise<void> {
    try {
      // 卸载插件
      await this.unloadPlugin(pluginName);

      // 删除插件文件
      const pluginPath = path.join(this.config.installDir, pluginName);
      if (await fs.pathExists(pluginPath)) {
        await fs.remove(pluginPath);
      }

      console.log(`✅ 插件 ${pluginName} 卸载成功`);
    } catch (error) {
      throw new PluginError(
        `卸载插件失败: ${error instanceof Error ? error.message : String(error)}`,
        pluginName,
        'UNINSTALL_FAILED'
      );
    }
  }

  /**
   * 更新插件
   */
  async updatePlugin(pluginName: string, version?: string): Promise<void> {
    try {
      // 卸载旧版本
      if (this.registry.has(pluginName)) {
        await this.unloadPlugin(pluginName);
      }

      // 安装新版本
      await this.installPlugin(pluginName, version);

      console.log(`✅ 插件 ${pluginName} 更新成功`);
    } catch (error) {
      throw new PluginError(
        `更新插件失败: ${error instanceof Error ? error.message : String(error)}`,
        pluginName,
        'UPDATE_FAILED'
      );
    }
  }

  /**
   * 列出已安装的插件
   */
  async listPlugins(): Promise<PluginMetadata[]> {
    const plugins = this.registry.getAll();
    return plugins.map(plugin => plugin.metadata);
  }

  /**
   * 检查插件状态
   */
  async checkPluginStatus(
    pluginName: string
  ): Promise<'installed' | 'outdated' | 'missing'> {
    if (!this.registry.has(pluginName)) {
      return 'missing';
    }

    // TODO: 实现版本检查逻辑
    return 'installed';
  }

  /**
   * 从 npm 安装插件
   */
  private async installFromNpm(
    pluginName: string,
    installPath: string,
    version?: string
  ): Promise<void> {
    // 创建安装目录
    await fs.ensureDir(installPath);

    // 模拟 npm 安装过程
    const packageJson = {
      name: pluginName,
      version: version || 'latest',
      dependencies: {
        [pluginName]: version || 'latest',
      },
    };

    await fs.writeJson(path.join(installPath, 'package.json'), packageJson, {
      spaces: 2,
    });

    // TODO: 实现真实的 npm 安装逻辑
    console.log(
      `📦 从 npm 安装插件: ${pluginName}${version ? `@${version}` : ''}`
    );
  }

  /**
   * 合并配置
   */
  private mergeConfig(config: Partial<PluginSystemConfig>): PluginSystemConfig {
    const defaultConfig: PluginSystemConfig = {
      searchPaths: [
        path.join(os.homedir(), '.synthia', 'plugins'),
        path.join(process.cwd(), 'node_modules'),
        path.join(process.cwd(), '.synthia', 'plugins'),
      ],
      installDir: path.join(os.homedir(), '.synthia', 'plugins'),
      cacheDir: path.join(os.homedir(), '.synthia', 'cache'),
      enabled: true,
      timeout: 30000,
      sandbox: true,
    };

    return { ...defaultConfig, ...config };
  }

  /**
   * 创建插件上下文
   */
  private createContext(): PluginContext {
    return {
      projectRoot: process.cwd(),
      configPath: path.join(process.cwd(), 'synthia.config.js'),
      globalOptions: {},
      logger: this.createLogger(),
      fs: this.createFileSystem(),
      http: this.createHttpClient(),
      cache: this.createCache(),
    };
  }

  /**
   * 创建日志记录器
   */
  private createLogger() {
    return {
      info: (message: string, ...args: any[]) =>
        console.log(`[INFO] ${message}`, ...args),
      warn: (message: string, ...args: any[]) =>
        console.warn(`[WARN] ${message}`, ...args),
      error: (message: string, ...args: any[]) =>
        console.error(`[ERROR] ${message}`, ...args),
      debug: (message: string, ...args: any[]) =>
        console.debug(`[DEBUG] ${message}`, ...args),
      success: (message: string, ...args: any[]) =>
        console.log(`✅ ${message}`, ...args),
    };
  }

  /**
   * 创建文件系统操作
   */
  private createFileSystem() {
    return {
      readFile: async (filePath: string, encoding = 'utf8') => {
        const content = await fs.readFile(filePath, encoding as any);
        return content.toString();
      },
      writeFile: async (
        filePath: string,
        content: string,
        encoding = 'utf8'
      ) => {
        await fs.writeFile(filePath, content, encoding as any);
      },
      exists: async (filePath: string) => {
        return await fs.pathExists(filePath);
      },
      mkdir: async (dirPath: string) => {
        await fs.ensureDir(dirPath);
      },
      readdir: async (dirPath: string) => {
        return await fs.readdir(dirPath);
      },
      remove: async (filePath: string) => {
        await fs.remove(filePath);
      },
      copy: async (src: string, dest: string) => {
        await fs.copy(src, dest);
      },
      move: async (src: string, dest: string) => {
        await fs.move(src, dest);
      },
    };
  }

  /**
   * 创建 HTTP 客户端
   */
  private createHttpClient() {
    return {
      get: async (_url: string, _options?: any) => {
        // TODO: 实现 HTTP GET 请求
        throw new Error('HTTP GET 请求未实现');
      },
      post: async (_url: string, _data?: any, _options?: any) => {
        // TODO: 实现 HTTP POST 请求
        throw new Error('HTTP POST 请求未实现');
      },
      put: async (_url: string, _data?: any, _options?: any) => {
        // TODO: 实现 HTTP PUT 请求
        throw new Error('HTTP PUT 请求未实现');
      },
      delete: async (_url: string, _options?: any) => {
        // TODO: 实现 HTTP DELETE 请求
        throw new Error('HTTP DELETE 请求未实现');
      },
    };
  }

  /**
   * 创建缓存操作
   */
  private createCache() {
    return {
      set: async (key: string, _value: any, _ttl?: number) => {
        // TODO: 实现缓存设置
        console.log(`设置缓存: ${key}`);
      },
      get: async (key: string) => {
        // TODO: 实现缓存获取
        console.log(`获取缓存: ${key}`);
        return null;
      },
      delete: async (key: string) => {
        // TODO: 实现缓存删除
        console.log(`删除缓存: ${key}`);
      },
      clear: async () => {
        // TODO: 实现缓存清空
        console.log('清空缓存');
      },
      has: async (key: string) => {
        // TODO: 实现缓存检查
        console.log(`检查缓存: ${key}`);
        return false;
      },
    };
  }
}
